// The app's only gateway to the chain: reads, quotes and every transaction a page can send.
// Pages talk to it through window.LC (plain JS); amounts go in as human strings or numbers.
import {
  BaseError,
  ContractFunctionRevertedError,
  UserRejectedRequestError,
  createPublicClient,
  createWalletClient,
  custom,
  encodeAbiParameters,
  formatEther,
  http,
  parseEther,
  parseSignature,
  type Address,
  type EIP1193Provider,
  type Hex,
  type WalletClient,
} from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import {
  listedFactoryAbi,
  listedGovernorAbi,
  listedHookAbi,
  listedIPOAbi,
  listedRouterAbi,
  listedTokenAbi,
} from '../../shared/abi';
import { chain, config } from './config';

const ALL_ABIS = [...listedRouterAbi, ...listedFactoryAbi, ...listedIPOAbi, ...listedGovernorAbi, ...listedHookAbi, ...listedTokenAbi];

export const publicClient = createPublicClient({ chain, transport: http(config.rpcUrl, { batch: true }) });

type Wallet = { address: Address; name: string; client: WalletClient; provider: EIP1193Provider | null };
let wallet: Wallet | null = null;
const listeners = new Set<(w: Wallet | null) => void>();

export function onWallet(fn: (w: Wallet | null) => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setWallet(address: Address, provider: EIP1193Provider, name: string) {
  wallet = {
    address,
    name,
    provider,
    client: createWalletClient({ account: address, chain, transport: custom(provider) }),
  };
  listeners.forEach((f) => f(wallet));
}

/** Dev-only automated-test wallet backed by a local private key. */
export function setTestWallet(pk: Hex) {
  const account = privateKeyToAccount(pk);
  wallet = { address: account.address, name: 'Test wallet', provider: null, client: createWalletClient({ account, chain, transport: http(config.rpcUrl) }) };
  listeners.forEach((f) => f(wallet));
}

export function clearWallet() {
  wallet = null;
  listeners.forEach((f) => f(null));
}

export function currentWallet() {
  return wallet ? { address: wallet.address, name: wallet.name } : null;
}

function need(): Wallet {
  if (!wallet) throw new Error('Connect a wallet first');
  return wallet;
}

function dep() {
  if (!config.deployment) throw new Error(`Listed is not deployed on chain ${config.chainId}`);
  return config.deployment;
}

// ------------------------------------------------------------------ network

export async function walletChainId(): Promise<number> {
  const w = need();
  if (!w.provider) return config.chainId;
  const id = (await w.provider.request({ method: 'eth_chainId' })) as string;
  return Number(id);
}

export async function onRightChain(): Promise<boolean> {
  return (await walletChainId()) === config.chainId;
}

export async function ensureChain(): Promise<void> {
  const w = need();
  if (!w.provider || (await onRightChain())) return;
  const hex = `0x${config.chainId.toString(16)}`;
  try {
    await w.provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] });
  } catch (e: any) {
    if (e?.code !== 4902 && e?.data?.originalError?.code !== 4902) throw e;
    await w.provider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: hex,
          chainName: config.chainName,
          nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
          rpcUrls: [config.rpcUrl],
          blockExplorerUrls: config.explorer ? [config.explorer] : undefined,
        },
      ],
    });
  }
}

export const explorerTx = (h: string) => (config.explorer ? `${config.explorer}/tx/${h}` : '');
export const explorerAddr = (a: string) => (config.explorer ? `${config.explorer}/address/${a}` : '');

// ------------------------------------------------------------------ helpers

const toWei = (v: string | number) => parseEther(String(v).trim() === '' ? '0' : String(v).replace(/,/g, '').trim());
const num = (v: bigint) => Number(formatEther(v));
const bps = (pct: number) => BigInt(Math.round(pct * 100));
/** 20 minutes from now, by whichever is later: our clock or the chain's (they can differ). */
async function deadline() {
  const blockTs = Number((await publicClient.getBlock()).timestamp);
  return BigInt(Math.max(Math.floor(Date.now() / 1000), blockTs) + 20 * 60);
}

async function write(address: Address, abi: readonly unknown[], functionName: string, args: unknown[], value?: bigint): Promise<Hex> {
  const w = need();
  await ensureChain();
  // Simulate first so a revert is explained before the wallet even opens.
  const { request } = await publicClient.simulateContract({
    address,
    abi: abi as any,
    functionName: functionName as any,
    args: args as any,
    value,
    account: w.client.account!,
  });
  // Gas depends on pool state, which another trade can change between the estimate and inclusion (and RPC nodes can
  // lag a block). Send with 30% headroom so a sell that crosses one more tick does not run out of gas; unused gas is not charged.
  const gas = await publicClient
    .estimateContractGas({ address, abi: abi as any, functionName: functionName as any, args: args as any, value, account: w.client.account! })
    .then((g) => (g * 13n) / 10n)
    .catch(() => undefined);
  return w.client.writeContract({ ...(request as any), ...(gas ? { gas } : {}) });
}

export async function waitFor(hash: Hex) {
  return publicClient.waitForTransactionReceipt({ hash, timeout: 180_000 });
}

/** A short sentence for any error a transaction can hit. */
export function errorText(e: unknown): string {
  if (e instanceof BaseError) {
    if (e.walk((x) => x instanceof UserRejectedRequestError)) return 'You rejected the request in your wallet.';
    const revert = e.walk((x) => x instanceof ContractFunctionRevertedError) as ContractFunctionRevertedError | null;
    const name = revert?.data?.errorName ?? '';
    const MAP: Record<string, string> = {
      Slippage: 'Price moved past your slippage. Nothing left your wallet. Try again or raise your slippage.',
      Expired: 'The transaction waited too long and expired. Try again.',
      WrongFee: 'The launch fee changed. Reload and try again.',
      SymbolTaken: 'That ticker is already listed. Pick another one.',
      InvalidSymbol: 'Tickers are 2 to 6 letters, A to Z.',
      InvalidName: 'Names are 1 to 32 bytes (emoji and accented letters count as more than one).',
      InvalidFee: 'The creator fee can be at most 2%.',
      FeeCanOnlyGoDown: 'After launch you can only lower your own cut of the fee. The treasury\'s cut can\'t go down without a holder vote.',
      NotCreator: 'Only the creator can do that.',
      OverWalletCap: 'That is over the per-wallet cap for this IPO.',
      TooEarly: 'It is not time yet.',
      TooLate: 'Too late: this window has closed.',
      BadStatus: 'This IPO is no longer taking that action.',
      NothingToClaim: 'Nothing to claim.',
      AlreadyVoted: 'You already voted on this.',
      NoPower: 'You have no voting power for this vote. Power is your average holding over the 7 days before it opened.',
      NotAllowed: 'Not allowed: you need 0.5% of voting power to propose (1% for Fire the CEO, a grant or a lock), or be the creator. You can have one open vote of each type per coin, and after your own failed Fire the CEO vote you wait 14 days to propose it again.',
      PriceMoved: 'The price has moved sharply in the last few minutes. Buybacks and liquidity boosts wait for a calm price so nobody can game them. It will go through once the price settles.',
      SwapViaRouterOnly: 'Listed coins trade only through Listed.',
      PriceLimitNotAllowed: 'Listed coins trade only through Listed.',
      NotDue: 'The next payout is not due yet.',
      TreasuryIsLocked: 'The treasury is locked by a holder vote until its unlock date.',
      CreatorWasFired: 'Holders fired this coin\'s creator, so there is no creator wallet any more.',
      TooManyPositions: 'This coin already has the most liquidity positions allowed (8).',
      BadState: 'This vote is not in the right state for that (a vote can only be withdrawn before anyone has voted).',
      BadParams: 'Those settings are not allowed. Check the limits shown for this vote type.',
      InsufficientBalance: 'Not enough tokens in your wallet.',
      InsufficientTreasury: 'The treasury does not have enough ETH for that.',
      PriceOutOfRange: 'The split would move the price out of range.',
    };
    if (name === 'BadParams' && /createIpo/.test(e.message)) return 'Those IPO settings are not allowed: the bell must be 1 hour to 30 days away, the minimum raise at least 0.01 ETH and the sale 10% to 80%.';
    if (name && MAP[name]) return MAP[name];
    if (/insufficient funds/i.test(e.message)) return 'Not enough ETH in your wallet to cover this and the network fee.';
    if (name) return `The transaction would fail (${name}).`;
    return e.shortMessage || e.message;
  }
  return (e as Error)?.message ?? String(e);
}

// ------------------------------------------------------------------ reads

export async function ethBalance(addr?: Address) {
  const a = addr ?? need().address;
  return num(await publicClient.getBalance({ address: a }));
}

export async function tokenBalance(token: Address, addr?: Address) {
  const a = addr ?? need().address;
  return num((await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'balanceOf', args: [a] })) as bigint);
}

export async function claimableDividend(token: Address, addr?: Address) {
  const a = addr ?? need().address;
  return num((await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'claimableDividendOf', args: [a] })) as bigint);
}

export async function quoteBuy(token: Address, ethIn: string | number) {
  const v = toWei(ethIn);
  if (v === 0n) return { tokens: 0, wei: 0n };
  const { result } = await publicClient.simulateContract({ address: dep().router, abi: listedRouterAbi, functionName: 'quoteBuy', args: [token, v] });
  return { tokens: num(result as bigint), wei: result as bigint };
}

export async function quoteSell(token: Address, qty: string | number) {
  const v = toWei(qty);
  if (v === 0n) return { eth: 0, wei: 0n };
  const { result } = await publicClient.simulateContract({ address: dep().router, abi: listedRouterAbi, functionName: 'quoteSell', args: [token, v] });
  return { eth: num(result as bigint), wei: result as bigint };
}

export async function feeBps(token: Address, buy: boolean) {
  const [total, tax] = (await publicClient.readContract({ address: dep().hook, abi: listedHookAbi, functionName: 'currentFeeBps', args: [token, buy] })) as [bigint, bigint];
  return { totalPct: Number(total) / 100, taxPct: Number(tax) / 100 };
}

export async function launchFees() {
  const [instant, ipo] = await Promise.all([
    publicClient.readContract({ address: dep().factory, abi: listedFactoryAbi, functionName: 'launchFee' }) as Promise<bigint>,
    publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'launchFee' }) as Promise<bigint>,
  ]);
  return { instant: num(instant), ipo: num(ipo) };
}

export async function startTick() {
  return Number(await publicClient.readContract({ address: dep().factory, abi: listedFactoryAbi, functionName: 'startTick' }));
}

export async function symbolAvailable(sym: string) {
  return (await publicClient.readContract({ address: dep().factory, abi: listedFactoryAbi, functionName: 'symbolAvailable', args: [sym.toUpperCase()] })) as boolean;
}

/** Latest block time in ms (chain time can differ from the browser clock). */
export async function chainTime() {
  return Number((await publicClient.getBlock()).timestamp) * 1000;
}

async function clampToChain(atMs: number) {
  return BigInt(Math.floor(Math.min(atMs, await chainTime()) / 1000));
}

/** Time-weighted voting power (coins, in shares at the time) of `addr` at `atMs`, clamped to the latest block. */
export async function votingPower(token: Address, atMs: number, addr?: Address) {
  const a = addr ?? need().address;
  const t = await clampToChain(atMs);
  return num((await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'votingPowerAt', args: [a, t] })) as bigint);
}

export async function totalVotingPower(token: Address, atMs: number) {
  const t = await clampToChain(atMs);
  return num((await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'totalVotingPowerAt', args: [t] })) as bigint);
}

export async function hasVoted(id: number, addr?: Address) {
  const a = addr ?? need().address;
  return (await publicClient.readContract({ address: dep().governor, abi: listedGovernorAbi, functionName: 'hasVoted', args: [BigInt(id), a] })) as boolean;
}

export async function creatorAccrued(token: Address) {
  return num((await publicClient.readContract({ address: dep().hook, abi: listedHookAbi, functionName: 'creatorAccrued', args: [token] })) as bigint);
}

export async function treasuryOf(token: Address) {
  return num((await publicClient.readContract({ address: dep().hook, abi: listedHookAbi, functionName: 'treasuryOf', args: [token] })) as bigint);
}

/** The IPO price ladder (ETH per token for each of the 256 levels), computed exactly like the contract. */
export async function ipoLadder(id: number): Promise<bigint[]> {
  const p0 = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'floorPrice', args: [BigInt(id)] })) as bigint;
  const out = [p0];
  for (let i = 1; i < 256; i++) out.push((out[i - 1] * 102n) / 100n);
  return out;
}

/** Lowest ladder level whose price is at least `maxPriceEth` (ETH per token). */
export async function ipoLevelFor(id: number, maxPriceEth: number) {
  const ladder = await ipoLadder(id);
  const target = BigInt(Math.ceil(maxPriceEth * 1e18));
  for (let i = 0; i < ladder.length; i++) if (ladder[i] >= target) return { level: i, price: num(ladder[i]) };
  return { level: 255, price: num(ladder[255]) };
}

export async function ipoAllocation(id: number, addr?: Address) {
  const a = addr ?? need().address;
  const [tokens, refund] = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'allocationOf', args: [BigInt(id), a] })) as [bigint, bigint];
  return { tokens: num(tokens), refund: num(refund) };
}

export async function ipoBid(id: number, addr?: Address) {
  const a = addr ?? need().address;
  const [amount, level, claimed] = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'bids', args: [BigInt(id), a] })) as [bigint, number, boolean];
  return { amount: num(amount), level: Number(level), claimed };
}

export async function ipoPreview(id: number) {
  const [ok, price, level, fill, raised] = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'previewClearing', args: [BigInt(id)] })) as [boolean, bigint, number, bigint, bigint];
  return { ok, price: num(price), level: Number(level), fill: num(fill), raised: num(raised) };
}

/** ETH bid at each of the 256 price levels. */
export async function ipoLevelDemand(id: number) {
  const d = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'levelDemand', args: [BigInt(id)] })) as readonly bigint[];
  return d.map(num);
}

export async function proposalState(id: number) {
  const s = (await publicClient.readContract({ address: dep().governor, abi: listedGovernorAbi, functionName: 'state', args: [BigInt(id)] })) as number;
  return ['none', 'active', 'canceled', 'defeated', 'queued', 'executable', 'executed', 'expired'][Number(s)];
}

// ------------------------------------------------------------------ writes (each resolves to a tx hash)

export async function buy(token: Address, ethIn: string | number, slippagePct: number) {
  const value = toWei(ethIn);
  const q = await quoteBuy(token, ethIn);
  const minOut = (q.wei * (10_000n - bps(slippagePct))) / 10_000n;
  return write(dep().router, listedRouterAbi, 'buy', [token, minOut, need().address, await deadline()], value);
}

/** Sells with a one-time permit signature, so there is no separate approve transaction. */
export async function sell(token: Address, qty: string | number, slippagePct: number, all = false) {
  const w = need();
  await ensureChain();
  let amount = toWei(qty);
  const bal = (await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'balanceOf', args: [w.address] })) as bigint;
  if (all || amount > bal) amount = bal;
  if (amount === 0n) throw new Error("You don't hold any of this coin to sell.");
  const q = await quoteSell(token, formatEther(amount));
  const minOut = (q.wei * (10_000n - bps(slippagePct))) / 10_000n;
  const router = dep().router;
  const allowance = (await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'allowance', args: [w.address, router] })) as bigint;
  const dl = await deadline();
  if (allowance >= amount) return write(router, listedRouterAbi, 'sell', [token, amount, minOut, w.address, dl]);
  const nonce = (await publicClient.readContract({ address: token, abi: listedTokenAbi, functionName: 'nonces', args: [w.address] })) as bigint;
  const sig = await w.client.signTypedData({
    account: w.client.account!,
    domain: { name: 'Listed Token', version: '1', chainId: config.chainId, verifyingContract: token },
    types: { Permit: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }, { name: 'value', type: 'uint256' }, { name: 'nonce', type: 'uint256' }, { name: 'deadline', type: 'uint256' }] },
    primaryType: 'Permit',
    message: { owner: w.address, spender: router, value: amount, nonce, deadline: dl },
  });
  const { r, s, v, yParity } = parseSignature(sig);
  return write(router, listedRouterAbi, 'sellWithPermit', [token, amount, minOut, w.address, dl, Number(v ?? BigInt(27 + (yParity ?? 0))), r, s]);
}

export interface LaunchInput {
  name: string;
  symbol: string;
  desc?: string;
  site?: string;
  x?: string;
  tg?: string;
  logo?: string | null;
  creatorFeePct: number;
  walletShare: number; // 0..1 of the creator fee to the creator's wallet
  initialBuyEth?: string | number;
}

async function uploadMeta(m: { desc?: string; site?: string; x?: string; tg?: string; logo?: string | null }) {
  const body: Record<string, string> = {};
  for (const k of ['desc', 'site', 'x', 'tg'] as const) if (m[k]) body[k] = m[k]!;
  if (m.logo) body.logo = m.logo;
  const r = await fetch(`${config.indexerUrl}/api/metadata`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Could not save the coin details');
  return ((await r.json()) as { uri: string }).uri;
}

export async function launch(i: LaunchInput) {
  const uri = await uploadMeta(i);
  const fee = (await publicClient.readContract({ address: dep().factory, abi: listedFactoryAbi, functionName: 'launchFee' })) as bigint;
  const value = fee + toWei(i.initialBuyEth ?? 0);
  return write(
    dep().factory,
    listedFactoryAbi,
    'launch',
    [{ name: i.name.trim(), symbol: i.symbol.toUpperCase(), metadataURI: uri, creatorBps: Number(bps(i.creatorFeePct)), walletShareBps: Math.round(i.walletShare * 10_000) }],
    value,
  );
}

export interface IpoInput extends LaunchInput {
  bellTime: number; // ms
  minRaiseEth: string | number;
  salePct: number; // 10..80
  maxPerWalletEth?: string | number; // 0 = no cap
}

export async function createIpo(i: IpoInput) {
  const uri = await uploadMeta(i);
  const fee = (await publicClient.readContract({ address: dep().ipo, abi: listedIPOAbi, functionName: 'launchFee' })) as bigint;
  return write(
    dep().ipo,
    listedIPOAbi,
    'createIpo',
    [
      {
        name: i.name.trim(),
        symbol: i.symbol.toUpperCase(),
        metadataURI: uri,
        bellTime: Math.floor(i.bellTime / 1000),
        minRaise: toWei(i.minRaiseEth),
        saleBps: Math.round(i.salePct * 100),
        maxPerWallet: toWei(i.maxPerWalletEth ?? 0),
        creatorBps: Number(bps(i.creatorFeePct)),
        walletShareBps: Math.round(i.walletShare * 10_000),
      },
    ],
    fee,
  );
}

/** Sets your bid to `amountEth` at the ladder level for `maxPriceEth`, sending only the difference. */
export async function setBid(id: number, amountEth: string | number, maxPriceEth: number) {
  const target = toWei(amountEth);
  const cur = await ipoBid(id);
  const curWei = toWei(cur.amount);
  const value = target > curWei ? target - curWei : 0n;
  const { level } = await ipoLevelFor(id, maxPriceEth);
  return write(dep().ipo, listedIPOAbi, 'setBid', [BigInt(id), target, level], value);
}

export const cancelBid = (id: number) => write(dep().ipo, listedIPOAbi, 'setBid', [BigInt(id), 0n, 0]);
export const cancelIpo = (id: number) => write(dep().ipo, listedIPOAbi, 'cancelIpo', [BigInt(id)]);
export const ringBell = (id: number) => write(dep().ipo, listedIPOAbi, 'settle', [BigInt(id)]);
export const expireIpo = (id: number) => write(dep().ipo, listedIPOAbi, 'expire', [BigInt(id)]);
export const claimIpo = (id: number) => write(dep().ipo, listedIPOAbi, 'claim', [BigInt(id)]);

const KINDS: Record<string, number> = {
  split: 1, reverse: 1, buyback: 2, dividend: 3, rebrand: 4, fee: 5, fire: 6, recurring: 7, lock: 8, grant: 9, boost: 10,
};
/** Votes with stricter rules always run the full 72 hours (ListedGovernor.rulesOf). */
export const STRICT_TYPES = ['fire', 'lock', 'grant'];

/**
 * Proposal params by type:
 *  split / reverse: {num, den}   e.g. {num:2,den:1} or {num:1,den:10}
 *  buyback (and burn): {eth, maxImpactPct}  dividend: {eth}
 *  rebrand: {name, sym}          fee: {feePct, walletShare}
 *  fire: {}                      recurring: {eth, intervalDays: 7|30, payouts: 2..12} (payouts 0 stops it)
 *  lock: {untilMs}               grant: {to, eth, vestingDays: 0..365}
 *  boost: {eth}
 */
export async function propose(token: Address, type: string, p: any, etaMs: number, votingHours?: number) {
  const kind = KINDS[type];
  if (!kind) throw new Error('Unknown action');
  let params: Hex;
  if (kind === 1) params = encodeAbiParameters([{ type: 'uint256' }, { type: 'uint256' }], [BigInt(p.num), BigInt(p.den)]);
  else if (kind === 2) params = encodeAbiParameters([{ type: 'uint256' }, { type: 'uint16' }], [toWei(p.eth), Math.round((p.maxImpactPct ?? 3) * 100)]);
  else if (kind === 3) params = encodeAbiParameters([{ type: 'uint256' }], [toWei(p.eth)]);
  else if (kind === 4) params = encodeAbiParameters([{ type: 'string' }, { type: 'string' }], [String(p.name), String(p.sym).toUpperCase()]);
  else if (kind === 5) params = encodeAbiParameters([{ type: 'uint16' }, { type: 'uint16' }], [Number(bps(p.feePct)), Math.round(p.walletShare * 10_000)]);
  else if (kind === 6) params = '0x';
  else if (kind === 7)
    params = encodeAbiParameters(
      [{ type: 'uint256' }, { type: 'uint256' }, { type: 'uint256' }],
      [p.payouts ? toWei(p.eth) : 0n, BigInt(p.payouts ? p.intervalDays : 0), BigInt(p.payouts || 0)],
    );
  else if (kind === 8) params = encodeAbiParameters([{ type: 'uint40' }], [Math.floor(p.untilMs / 1000)]);
  else if (kind === 9)
    params = encodeAbiParameters([{ type: 'address' }, { type: 'uint256' }, { type: 'uint256' }], [p.to, toWei(p.eth), BigInt(p.vestingDays || 0)]);
  else params = encodeAbiParameters([{ type: 'uint256' }], [toWei(p.eth)]);
  const eta = Math.floor(etaMs / 1000);
  // Vote length: 6, 12, 24, 48 or 72 hours (of a protocol day), chosen by the proposer. Without one the governor uses 72 hours.
  if (votingHours != null) return write(dep().governor, listedGovernorAbi, 'propose', [token, kind, params, eta, Math.round((votingHours / 24) * (Number((dep() as any).day) || 86_400))]);
  return write(dep().governor, listedGovernorAbi, 'propose', [token, kind, params, eta]);
}

export const vote = (id: number, support: 'for' | 'against' | 'abstain') =>
  write(dep().governor, listedGovernorAbi, 'castVote', [BigInt(id), support === 'for' ? 1 : support === 'against' ? 0 : 2]);
export const executeVote = (id: number) => write(dep().governor, listedGovernorAbi, 'execute', [BigInt(id)]);
export const cancelVote = (id: number) => write(dep().governor, listedGovernorAbi, 'cancel', [BigInt(id)]);
export const claimDividend = (token: Address) => write(token, listedTokenAbi, 'claimDividend', []);
export const claimCreatorFees = (token: Address) => write(dep().hook, listedHookAbi, 'claimCreatorFees', [token]);
export const lowerCreatorFee = (token: Address, feePct: number, walletShare: number) =>
  write(dep().hook, listedHookAbi, 'lowerCreatorFee', [token, Number(bps(feePct)), Math.round(walletShare * 10_000)]);
export const burn = (token: Address, amount: number) => write(token, listedTokenAbi, 'burn', [toWei(amount)]);
export const claimGrant = (grantId: number) => write(dep().governor, listedGovernorAbi, 'claimGrant', [BigInt(grantId)]);
export const payRecurring = (token: Address) => write(dep().governor, listedGovernorAbi, 'payRecurring', [token]);
export async function grantClaimable(grantId: number) {
  const g = (await publicClient.readContract({ address: dep().governor, abi: listedGovernorAbi, functionName: 'grantOf', args: [BigInt(grantId)] })) as any;
  const vested = (await publicClient.readContract({ address: dep().governor, abi: listedGovernorAbi, functionName: 'vestedOf', args: [BigInt(grantId)] })) as bigint;
  return { recipient: String(g.recipient).toLowerCase(), amount: num(g.amount), claimed: num(g.claimed), claimable: num(vested - g.claimed) };
}
export const publishEarnings = (token: Address) => write(dep().hook, listedHookAbi, 'publishEarnings', [token]);

export const abis = ALL_ABIS;
